import { useContext, useEffect, useState } from 'react';
import "../Announcement/Announcement.scss";
import { toast } from 'react-toastify';
import { confirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { inputStyles, labelStyles } from '../Announcement/AnnouncementStyles';
import { AppContext } from '../../../context/AppContext';
import api from '../../../api.js';
import { Dropdown } from 'primereact/dropdown';
import "./GalleryPage.scss";
import Loader from '../../../components/Loader/Loader.jsx';

const GalleryPage = () => {
    const [allGalleryImages, setAllGalleryImages] = useState([]);
    const { backendUrl } = useContext(AppContext);
    const [galleryImage, setGalleryImage] = useState({
        imageFile: null,
        layoutType: "",
        layoutPosition: "",
    });
    const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
    const [updatedGalleryImage, setUpdatedGalleryImage] = useState({
        imageFile: null,
        layoutType: "",
        layoutPosition: "",
    });
    const [visible, setVisible] = useState(false);
    const [validationError, setValidationError] = useState({});
    const [loading, setLoading] = useState(false);
    const [scrollHeight, setScrollHeight] = useState("425px");
    const [formLoading, setFormLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    let errorShown = false;

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerHeight;
            if (screenWidth >= 900) {
                setScrollHeight("650px");
            } else {
                setScrollHeight("440px");
            }
        }
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const layoutTypes = [
        { label: "Top", value: "top" },
        { label: "Banner", value: "banner" },
        { label: "Third Row", value: "thirdRow" },
        { label: "Fourth Row", value: "fourthRow" }
    ];
    const layoutPositions = [
        { label: "Right", value: "right" },
        { label: "Left", value: "left" }
    ];

    async function fetchAllGalleryImages() {
        setLoading(true);
        
        try {
            const { data } = await api.get(`${backendUrl}/api/auth/getAllGalleryImages`);
            if (data.success) {
                setAllGalleryImages(data || []);
            }
            errorShown = false;
        } catch (error) {
            if (errorShown) return;
            errorShown = true;
            if (error.response?.status === 503) {
                toast.error("⚠️ Service under maintenance. Please try later.");
            } else if (!navigator.onLine) {
                toast.error("🚫 No internet connection");
            } else if (error.request && !error.response) {
                toast.error("⚠️ Network issue. Try again later.");
            } else {
                console.error("Error:", error);
                toast.error("Something went wrong.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllGalleryImages();
    }, []);

    const validateFile = (image, isEditMode = false) => {
        if (!isEditMode && !image) {
            return "Please select an image to upload";
        }
        if (image) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(image.type)) {
                return "Only JPG, PNG, GIF, and WEBP images allowed";
            }
            if (image.size > 25 * 1024 * 1024) {
                return "Image size must be less than 25MB";
            }
        }
        return null;
    };

    const validateGalleryImage = (imageData, isEditMode = false) => {
        const errors = {};
        if (!imageData) {
            errors.general = "Form data is missing";
            return errors;
        }
        const fileError = validateFile(imageData.imageFile, isEditMode);
        if (fileError) errors.imageFile = fileError;
        if (!imageData.layoutType) {
            errors.layoutType = "Layout type is required";
        }
        if (["thirdRow", "fourthRow"].includes(imageData.layoutType) && !imageData.layoutPosition) {
            errors.layoutPosition = "Layout position is required";
        }
        return errors;
    };

    async function uploadGalleryImage(e) {
        e.preventDefault();
        const errors = validateGalleryImage(galleryImage);
        if (Object.keys(errors).length > 0) {
            setValidationError(errors);
            return;
        }
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("imageFile", galleryImage.imageFile);
            formData.append("layoutType", galleryImage.layoutType);
            formData.append("layoutPosition", galleryImage.layoutPosition || "");
            const { data } = await api.post(
                `${backendUrl}/api/auth/uploadAGalleryImage`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (data.success) {
                toast.success(data.message);
                fetchAllGalleryImages();
                resetForm();
            }
        } catch (error) {
            if (errorShown) return;
            errorShown = true;
            if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }
            else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            }
            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else {
                toast.error("Something went wrong.");
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const updateGalleryImage = async (e) => {
        e.preventDefault();
        const errors = validateGalleryImage(updatedGalleryImage, true);
        if (Object.keys(errors).length > 0) {
            setValidationError(errors);
            return;
        }
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            if (updatedGalleryImage.imageFile) {
                formData.append("imageFile", updatedGalleryImage.imageFile);
            }
            formData.append("layoutType", updatedGalleryImage.layoutType);
            formData.append("layoutPosition", updatedGalleryImage.layoutPosition || "");
            const { data } = await api.put(
                `${backendUrl}/api/auth/updateAGalleryImage/${selectedGalleryImage.id}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            if (data.success) {
                toast.success(data.message);
                fetchAllGalleryImages();
                resetForm();
            }
        } catch (error) {
            if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }
            else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            }
            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else {
                toast.error("Something went wrong.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormLoading(true);
        setVisible(false);
        setTimeout(() => {
            setSelectedGalleryImage(null);
            setGalleryImage({
                imageFile: null,
                layoutType: "",
                layoutPosition: ""
            });
            setUpdatedGalleryImage({
                imageFile: null,
                layoutType: "",
                layoutPosition: ""
            });
            setValidationError({});
            setFormLoading(false);
        }, 100);
    };

    const handleApiError = (error) => {
        if (error.response?.status === 503) {
            toast.error("⚠️ Service under maintenance");
        } else if (!navigator.onLine) {
            toast.error("🚫 No internet connection");
        } else if (error.request && !error.response) {
            toast.error("⚠️ Network issue");
        } else {
            console.error("Error:", error);
            toast.error("Something went wrong", error);
        }
    };

    const showConfirmDialog = (rowData) => {
        confirmDialog({
            message: (
                <div className="custom-dialog-content">
                    <div className="iconParent">
                        <i className="pi pi-times"></i>
                    </div>
                    <div className="contentParent">
                        <h2>Are you sure?</h2>
                        <p>Do you really want to delete this Image?</p>
                        <p>This process cannot be undone.</p>
                    </div>
                </div>
            ),
            header: '',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger custom-delete-btn',
            rejectClassName: 'p-button-secondary custom-cancel-btn',
            accept: () => handleDeleteClick(rowData)
        });
    };

    async function handleDeleteClick(rowData) {
        try {
            const { data } = await api.delete(`${backendUrl}/api/auth/deleteAGalleryImage/${rowData.id}`);
            if (data.success) {
                toast.success(data.message);
                fetchAllGalleryImages();
            }
        } catch (error) {
            handleApiError(error);
        }
    }

    const handleEditClick = (rowData) => {
        setSelectedGalleryImage(rowData);
        setUpdatedGalleryImage({
            imageFile: null,
            layoutType: rowData.layoutType?.toLowerCase() || "",
            layoutPosition: rowData.layoutPosition?.toLowerCase() || "",
        });
        setVisible(true);
    };

    const toggleStatus = async (rowData) => {
        setIsSubmitting(true);
        try {
            const response = await api.patch(
                `${backendUrl}/api/auth/toggleGalleryImageStatus/${rowData.id}`,
                { isActive: !rowData.isActive }
            );
            fetchAllGalleryImages();
            if (response.data.success) {
                toast.success(response.data.message)
            }
        } catch (error) {
            handleApiError(error);
        } finally {
      setIsSubmitting(false);
    }
    };

    const capitalize = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const formatLayoutType = (str) => {
        if (!str) return "";
        switch (str) {
            case "top":
                return "Top";
            case "banner":
                return "Banner";
            case "thirdRow":
                return "Third Row";
            case "fourthRow":
                return "Fourth Row";
            default:
                return capitalize(str);
        }
    };

    return (
        <div className="adminMinutesMainContainer">
                  <Loader visible={isSubmitting} />

            <div className="headerParent">
                <h2>Welcome to Admin Gallery Images Page</h2>
            </div>

            <Dialog
                header={selectedGalleryImage ? "Edit Image" : "Upload Image"}
                visible={visible}
                style={{ width: '40vw', minHeight: "fit-content" }}
                onHide={resetForm}
                draggable={false}
                resizable={false}
                modal
                className="customAnnouncementDialog"
            >
                {!formLoading && (
                    <form onSubmit={selectedGalleryImage ? updateGalleryImage : uploadGalleryImage}>

                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <label style={labelStyles} htmlFor="layoutType">
                                Layout
                                {!selectedGalleryImage && <sup style={{ fontWeight: "700", color: "red", fontSize: "1.3rem" }}>*</sup>}
                            </label>
                            <Dropdown
                                value={selectedGalleryImage
                                    ? updatedGalleryImage?.layoutType ?? ""
                                    : galleryImage?.layoutType ?? ""}
                                onChange={(e) => {
                                    const val = e.value;
                                    selectedGalleryImage
                                        ? setUpdatedGalleryImage(prev => ({ ...prev, layoutType: val }))
                                        : setGalleryImage(prev => ({ ...prev, layoutType: val }));
                                }}
                                options={layoutTypes}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select layout type"
                                className="w-full md:w-14rem layoutDropdown"
                                appendTo="self"
                            />
                            {validationError.layoutType && (
                                <div className="validation-error">
                                    <i className="pi pi-exclamation-triangle"></i> {validationError.layoutType}
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <label style={labelStyles}>
                                Position
                                {(galleryImage.layoutType === "thirdRow" || galleryImage.layoutType === "fourthRow") && <sup style={{ fontWeight: "700", color: "red", fontSize: "1.3rem" }}>*</sup>}
                            </label>
                            <Dropdown
                                value={selectedGalleryImage
                                    ? updatedGalleryImage?.layoutPosition ?? ""
                                    : galleryImage?.layoutPosition ?? ""}
                                onChange={(e) => {
                                    const val = e.value;
                                    selectedGalleryImage
                                        ? setUpdatedGalleryImage(prev => ({ ...prev, layoutPosition: val }))
                                        : setGalleryImage(prev => ({ ...prev, layoutPosition: val }));
                                }}
                                options={layoutPositions}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select layout position"
                                className="w-full md:w-14rem layoutPosition"
                                disabled={
                                    selectedGalleryImage
                                        ? !["thirdRow", "fourthRow"].includes(updatedGalleryImage?.layoutType ?? "")
                                        : !["thirdRow", "fourthRow"].includes(galleryImage?.layoutType ?? "")
                                }
                                appendTo="self"
                            />
                            {validationError.layoutPosition && (
                                <div className="validation-error">
                                    <i className="pi pi-exclamation-triangle"></i> {validationError.layoutPosition}
                                </div>
                            )}
                        </div>

                        <div className="inputFieldWrapper">
                            <label style={labelStyles}>
                                Upload New Image
                                {!selectedGalleryImage && (
                                    <sup style={{ fontWeight: "700", color: "red", fontSize: "1.3rem" }}>*</sup>
                                )}
                            </label>
                            {selectedGalleryImage && (
                                <div style={{
                                    margin: '8px 0px',
                                    fontFamily: "var(--font-paragraph)",
                                    fontSize: "var(--paragraph-font-size)"
                                }}>
                                    Current file: {selectedGalleryImage.filename}
                                </div>
                            )}
                            <input
                                type="file"
                                accept=".jpg, .jpeg, .png, .gif, .webp"
                                style={inputStyles}
                                name="imageFile"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const error = validateFile(file, !!selectedGalleryImage);
                                        if (error) {
                                            setValidationError({ imageFile: error });
                                            e.target.value = '';
                                            return;
                                        }
                                        setValidationError(prev => ({ ...prev, imageFile: null }));
                                    }

                                    selectedGalleryImage
                                        ? setUpdatedGalleryImage(prev => ({ ...prev, imageFile: file }))
                                        : setGalleryImage(prev => ({ ...prev, imageFile: file }));
                                }}
                            />
                            {validationError.imageFile && (
                                <div className="validation-error">
                                    <i className="pi pi-exclamation-triangle"></i> {validationError.imageFile}
                                </div>
                            )}
                        </div>

                        <div className="submitBtnParent">
                            <button
                                type="submit"
                                className="submitButton"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="pi pi-spinner pi-spin"></i> Processing...
                                    </>
                                ) : selectedGalleryImage ? (
                                    "Update Image"
                                ) : (
                                    "Upload Image"
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </Dialog>

            <div className="uploadBtnsParent">
                <Button label="Upload An Image" onClick={() => setVisible(true)} className="uploadBtn" />
            </div>

            <article className="tableWrapper">
                <div className="scrollableTableContainer">
                    <DataTable
                        value={allGalleryImages.data}
                        showGridlines
                        stripedRows
                        className="table"
                        emptyMessage="No images available"
                        paginator
                        rows={5}
                        rowsPerPageOptions={[5, 10, 20]}
                        scrollable
                        scrollHeight={scrollHeight}
                        loading={loading}
                    >
                        <Column
                            header="S.No"
                            body={(_, options) => options.rowIndex + 1} className="tableData"
                            headerStyle={{ whiteSpace: 'nowrap' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                        />
                        <Column
                            header="Image"
                            className="tableData"
                            style={{ minWidth: '300px', maxWidth: '350px' }}
                            body={(rowData) => {
                                if (!rowData.data || !rowData.filename || !rowData.contentType) {
                                    return <span>No Image</span>;
                                }
                                const byteCharacters = atob(rowData.data);
                                const byteNumbers = Array.from(byteCharacters).map(char => char.charCodeAt(0));
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: rowData.contentType });
                                const downloadUrl = URL.createObjectURL(blob);
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>{rowData.filename}</span>
                                        <a
                                            href={downloadUrl}
                                            download={rowData.filename}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-pr-tooltip="Download file"
                                            data-pr-position="top"
                                        >
                                            <Button
                                                icon="pi pi-download"
                                                rounded
                                                text
                                                severity="info"
                                                aria-label="Download"
                                                className="download-button"
                                            />
                                        </a>
                                    </div>
                                );
                            }}
                        />
                        <Column
                            field="layoutType"
                            header="Layout Type"
                            className="tableData"
                            style={{ minWidth: "150px" }}
                            headerStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                            body={(rowData) => formatLayoutType(rowData.layoutType)}
                        />

                        <Column
                            field="layoutPosition"
                            header="Layout Position"
                            className="tableData"
                            style={{ minWidth: "175px" }}
                            headerStyle={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                            body={(rowData) => capitalize(rowData.layoutPosition)}
                        />
                        <Column
                            field="createdAt"
                            header="Created At"
                            body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                            className="tableData"
                            headerStyle={{ whiteSpace: 'nowrap' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                        />
                        <Column
                            field="updatedAt"
                            header="Updated At"
                            body={(rowData) => new Date(rowData.updatedAt).toLocaleDateString()}
                            className="tableData"
                            headerStyle={{ whiteSpace: 'nowrap' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                        />
                        <Column
                            field="status"
                            header="Status"
                            headerStyle={{ whiteSpace: 'nowrap' }}
                            bodyStyle={{ whiteSpace: 'nowrap' }}
                            className="tableData"
                            body={(rowData) => (
                                <div className="statusBtnParent">
                                    <Button
                                        label={rowData.isActive ? "Active" : "Deactive"}
                                        severity={rowData.isActive ? "success" : "danger"}
                                        className="statusBtn"
                                        onClick={() => toggleStatus(rowData)}
                                    />
                                </div>
                            )}
                        />
                        <Column
                            header="Action"
                            body={(rowData) => (
                                <div className="action-buttons">
                                    <Button label="Edit" severity="success" className="actionBtns" onClick={() => handleEditClick(rowData)} />
                                    <Button label="Delete" severity="danger" className="actionBtns" onClick={() => showConfirmDialog(rowData)} />
                                </div>
                            )}
                            className="tableData"
                        />
                    </DataTable>
                </div>
            </article>
        </div>
    );
};

export default GalleryPage;